import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Editor } from "@tinymce/tinymce-react";
import { FONT_FAMILIES } from "../../../utils/fontFamilies";

import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/media";
import "tinymce/plugins/charmap";
import "tinymce/plugins/preview";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/code";
import "tinymce/plugins/table";
import "tinymce/plugins/wordcount";
import "tinymce/plugins/quickbars";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/directionality";
import "tinymce/plugins/fullscreen";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/ui/oxide/content.min.css";
import "tinymce/skins/content/default/content.css";

const FONT_SIZES = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"];

const LessonRichTextEditor = ({ value, onChange, minHeight = 600, disabled = false }) => {
  const defaultFontStack = useMemo(() => {
    if (Array.isArray(FONT_FAMILIES) && FONT_FAMILIES.length > 0) {
      return FONT_FAMILIES[0].stack;
    }
    return "Helvetica Neue, Helvetica, Arial, sans-serif";
  }, []);

  const fontFormats = useMemo(() => {
    if (!Array.isArray(FONT_FAMILIES)) {
      return "";
    }

    return FONT_FAMILIES.map((font) => {
      const label = font.label || font.value || "Font";
      const stack = font.stack || font.value || label;
      return `${label}=${stack}`;
    }).join("; ");
  }, []);

  const fontsizeFormats = useMemo(() => FONT_SIZES.join(" "), []);

  const editorConfig = useMemo(
    () => ({
      height: minHeight,
      min_height: minHeight,
      menubar: false,
      branding: false,
      promotion: false,
      convert_urls: false,
      plugins: [
        "advlist",
        "autolink",
        "lists",
        "link",
        "image",
        "media",
        "charmap",
        "preview",
        "anchor",
        "searchreplace",
        "visualblocks",
        "code",
        "table",
        "wordcount",
        "quickbars",
        "autoresize",
        "directionality",
        "fullscreen",
      ],
      toolbar:
        "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | ltr rtl | bullist numlist outdent indent | link image media | table | removeformat | code preview fullscreen",
      toolbar_mode: "sliding",
      quickbars_selection_toolbar:
        "bold italic underline | forecolor backcolor | quicklink blockquote",
      table_toolbar:
        "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells",
      table_default_styles: {
        width: "100%",
        borderCollapse: "collapse",
      },
      table_default_attributes: {
        class: "lesson-table",
      },
      content_style: `
        body { font-family: ${defaultFontStack}; color: #1f2933; }
        .lesson-table { width: 100%; border-collapse: collapse; }
        .lesson-table th,
        .lesson-table td { border: 1px solid #d0d7de; padding: 8px 12px; min-width: 40px; }
        .lesson-table th { background-color: #f9fafb; }
        p { margin: 0; }
      `,
      font_formats: fontFormats,
      font_family_formats: fontFormats,
      fontsize_formats: fontsizeFormats,
      font_size_formats: fontsizeFormats,
      autoresize_bottom_margin: 16,
      autoresize_min_height: minHeight,
      importcss_append: true,
      skin: false,
      content_css: false,
      license_key: "gpl",
    }),
    [defaultFontStack, fontFormats, fontsizeFormats, minHeight]
  );

  return (
    <Editor
      value={value ?? ""}
      disabled={disabled}
      onEditorChange={(content) => {
        if (typeof onChange === "function") {
          onChange(content);
        }
      }}
      init={editorConfig}
    />
  );
};

LessonRichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  minHeight: PropTypes.number,
  disabled: PropTypes.bool,
};

export default LessonRichTextEditor;
